// Cart page (issue 33): the quantity buttons, and saving a changed quantity straight away.
// Since issue 186 the cart updates in place: quick taps are gathered for a moment, sent in one
// /cart/update.js call, and the section comes back rendered by Shopify (its rules, fees and
// "checkout" state are worked out in Liquid, as before). If that fails, the form posts as it did.
// Since issue 362, Remove works the same way, sent at once: the row fades as it's tapped, and the
// page doesn't reload (it took about a second). If that fails, the form posts with it at 0.
// Issue 446: a Delhi NCR cart shows its progress bar toward the minimum and free delivery.

import { cartGoal, goalHtml } from "@cck/cart-goal-core";
import { regionLabel } from "@cck/region-core";

let timer = 0;
let busy = false;

function sectionEl() {
  return document.querySelector("[data-cck-cart]");
}

async function sendUpdates(/** @type {HTMLFormElement} */ form) {
  const root = sectionEl();
  const id = root?.getAttribute("data-cck-section");
  const qty = [...form.querySelectorAll("[data-cck-cart-qty]")].map((i) =>
    Math.min(50, Math.max(0, Number(/** @type {HTMLInputElement} */ (i).value || 0))),
  );
  if (!root || !id) return form.requestSubmit(updateButton(form));
  busy = true;
  root.setAttribute("aria-busy", "true");
  try {
    const res = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: qty, sections: id }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const cart = await res.json();
    const html = cart.sections?.[id];
    if (!html) throw new Error("no section");
    const fresh = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector("[data-cck-cart]");
    if (!fresh) throw new Error("no cart in the section");
    root.replaceWith(fresh);
    const count = document.querySelector(".cck-header__count");
    const link = document.querySelector(".cck-header__cart");
    if (count) count.textContent = String(cart.item_count);
    link?.setAttribute(
      "aria-label",
      `Cart, ${cart.item_count} ${cart.item_count === 1 ? "item" : "items"}`,
    );
    wire();
    showGoal();
    // The new cart's dispatch date and delivery fee are filled from the customer's menu, as on
    // load: announce it again (cck-region.js keeps the latest in window.CCK_OUTCOME).
    const o = /** @type {any} */ (window).CCK_OUTCOME;
    if (o)
      document.dispatchEvent(
        new CustomEvent("cck:region", {
          detail: {
            ...o,
            region: o.region === "none" ? null : o.region,
            label: regionLabel(o),
            outcome: o,
          },
        }),
      );
  } catch {
    form.requestSubmit(updateButton(form));
  } finally {
    busy = false;
  }
}

/** The progress bar, with the customer's band fee once the pincode gate knows it. */
function showGoal() {
  const box = document.querySelector("[data-cck-cart-goal]");
  if (!(box instanceof HTMLElement)) return;
  const o = /** @type {any} */ (window).CCK_OUTCOME;
  const goal = cartGoal({
    total: Number(box.dataset.total),
    minimum: Number(box.dataset.minimum),
    freeFrom: Number(box.dataset.freeFrom),
    fee: o?.region === "ncr" ? o.fee : null,
  });
  if (!goal) return;
  box.innerHTML = goalHtml(goal);
  // The bar says what's missing; the list's minimum line would say it twice.
  for (const li of document.querySelectorAll("[data-cck-goal-covers]"))
    if (li instanceof HTMLElement) li.hidden = true;
  const list = document.querySelector(".cck-cart__problems");
  if (list instanceof HTMLElement)
    list.hidden = ![...list.children].some((c) => c instanceof HTMLElement && !c.hidden);
}

function updateButton(/** @type {HTMLFormElement} */ form) {
  const b = form.querySelector("[data-cck-cart-update]");
  return b instanceof HTMLButtonElement ? b : undefined;
}

function wire() {
  const form = document.querySelector("[data-cck-cart-form]");
  if (!(form instanceof HTMLFormElement)) return;
  const later = () => {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      if (!busy) sendUpdates(form);
    }, 450);
  };
  for (const b of form.querySelectorAll("[data-cck-cart-step]")) {
    b.addEventListener("click", () => {
      const input = b.parentElement?.querySelector("[data-cck-cart-qty]");
      if (!(input instanceof HTMLInputElement)) return;
      const next = Number(input.value || 0) + Number(b.getAttribute("data-cck-cart-step"));
      input.value = String(Math.min(50, Math.max(0, next)));
      later();
    });
  }
  for (const input of form.querySelectorAll("[data-cck-cart-qty]"))
    input.addEventListener("change", later);
  for (const link of form.querySelectorAll(".cck-cart__remove")) {
    link.addEventListener("click", (e) => {
      const row = link.closest(".cck-cart__item");
      const input = row?.querySelector("[data-cck-cart-qty]");
      if (busy || !(input instanceof HTMLInputElement)) return; // the link, as before
      e.preventDefault();
      clearTimeout(timer);
      input.value = "0";
      row?.classList.add("cck-cart__item--removing");
      link.textContent = "Removing…";
      sendUpdates(form);
    });
  }
}

wire();
showGoal();
document.addEventListener("cck:region", showGoal);
